import React from "react";
import { Outlet, NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import ThemeInjector from "@/components/ThemeInjector";
import {
  LayoutDashboard, BookOpen, Users, Briefcase, FileText, Calendar,
  Image as ImageIcon, MessageSquare, Handshake, HelpCircle, Images,
  Settings as SettingsIcon, LogOut, Menu as MenuIcon, ExternalLink, FileEdit, Inbox,
} from "lucide-react";

const NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true, testId: "admin-nav-dashboard" },
  { to: "/admin/books", icon: BookOpen, label: "Livros", testId: "admin-nav-books" },
  { to: "/admin/authors", icon: Users, label: "Autores", testId: "admin-nav-authors" },
  { to: "/admin/services", icon: Briefcase, label: "Serviços", testId: "admin-nav-services" },
  { to: "/admin/blog", icon: FileText, label: "Blog", testId: "admin-nav-blog" },
  { to: "/admin/events", icon: Calendar, label: "Eventos", testId: "admin-nav-events" },
  { to: "/admin/gallery", icon: Images, label: "Galeria", testId: "admin-nav-gallery" },
  { to: "/admin/testimonials", icon: MessageSquare, label: "Depoimentos", testId: "admin-nav-testimonials" },
  { to: "/admin/partners", icon: Handshake, label: "Parceiros", testId: "admin-nav-partners" },
  { to: "/admin/faqs", icon: HelpCircle, label: "FAQ", testId: "admin-nav-faqs" },
  { to: "/admin/banners", icon: ImageIcon, label: "Banners", testId: "admin-nav-banners" },
  { to: "/admin/menus", icon: MenuIcon, label: "Menus", testId: "admin-nav-menus" },
  { to: "/admin/pages", icon: FileEdit, label: "Páginas", testId: "admin-nav-pages" },
  { to: "/admin/submissions", icon: Inbox, label: "Formulários", testId: "admin-nav-submissions" },
  { to: "/admin/settings", icon: SettingsIcon, label: "Configurações", testId: "admin-nav-settings" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen flex bg-[color:var(--marupiara-obsidian)] text-[color:var(--marupiara-ivory)]">
      <ThemeInjector />
      <aside className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-72 bg-[color:var(--marupiara-charcoal)] border-r border-[color:var(--marupiara-gold)]/15 transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="p-6 border-b border-[color:var(--marupiara-gold)]/15">
          <Link to="/admin" className="flex items-center gap-3" data-testid="admin-logo-link">
            {settings.logo_url ? (
              <img src={settings.logo_url} alt="Logo" className="h-10 w-10 rounded-full" style={{ background: "#0A0A0A" }} />
            ) : null}
            <div>
              <div className="font-serif text-lg text-[color:var(--marupiara-gold)]">Marupiara</div>
              <div className="overline text-[10px] text-white/70">Painel Admin</div>
            </div>
          </Link>
        </div>
        <nav className="p-4 space-y-1 overflow-y-auto h-[calc(100vh-200px)]">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testId}
              className={({ isActive }) => `admin-nav-link ${isActive ? "active" : ""}`}
              onClick={() => setOpen(false)}
            >
              <item.icon size={16} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[color:var(--marupiara-gold)]/15">
          <a href="/" target="_blank" rel="noreferrer" data-testid="admin-view-site" className="admin-nav-link">
            <ExternalLink size={16} /> Ver site
          </a>
          <button data-testid="admin-logout" onClick={handleLogout} className="admin-nav-link w-full text-left">
            <LogOut size={16} /> Sair
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 lg:ml-0">
        <header className="sticky top-0 z-30 bg-[color:var(--marupiara-charcoal)]/80 backdrop-blur border-b border-[color:var(--marupiara-gold)]/15 px-6 py-3 flex items-center justify-between">
          <button data-testid="admin-menu-toggle" onClick={() => setOpen(!open)} className="lg:hidden text-[color:var(--marupiara-gold)]"><MenuIcon /></button>
          <div className="text-sm text-white/60">Olá, <span className="text-[color:var(--marupiara-gold)]">{user?.name}</span></div>
        </header>
        <main className="p-6 lg:p-10 min-h-[calc(100vh-56px)]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
